<script setup>
import {computed, onMounted} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableTextEditorField from "../../../portal-editable-fields/EditableTextEditorField.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import ReferralPortalResources from "./includes/ReferralPortalResources.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

// Component mounted
onMounted(() => {
    // Component ready
});

// Get program and settings
const program = computed(() => portalContentAndDesignStore.program);
const landingPageResources = computed(() => portalContentAndDesignStore.landingPageResources || {});

// Computed styles based on portal settings
const getContentContainerClasses = computed(() => {
    const settings = portalContentAndDesignStore;
    const layoutStyle = settings.portalLayoutSettings?.referral_portal_content_layout_style || 'border';
    const corners = settings.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const borderColor = settings.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
    const backgroundColor = settings.portalLayoutSettings?.referral_portal_content_background_color || 'white';
    
    // Background color with custom color support
    let classes = `bg-${backgroundColor.startsWith('#') ? 'content-custom-bg' : backgroundColor} ${layoutStyle}`;
    
    if (layoutStyle === 'border') {
        // Border color with custom color support
        if (borderColor.startsWith('#')) {
            classes += ` border border-content-custom-color`;
        } else {
            classes += ` border border-${borderColor}`;
        }
    } else if (layoutStyle === 'shadow') {
        classes += ' border shadow-lg';
    }
    
    classes += ` ${corners === 'none' ? 'rounded-sm' : corners}`;
    
    return classes;
});

const getHeaderClasses = computed(() => {
    const settings = portalContentAndDesignStore;
    const layoutStyle = settings.portalLayoutSettings?.referral_portal_content_layout_style || 'border';
    const borderColor = settings.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
    
    let classes = 'px-4 sm:px-8 lg:px-14 py-4 sm:py-8 lg:py-14';
    
    if (layoutStyle === 'border') {
        // Border color with custom color support
        if (borderColor.startsWith('#')) {
            classes += ` border-b border-content-custom-color`;
        } else {
            classes += ` border-b border-${borderColor}`;
        }
    }
    
    return classes;
});

const getTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-content-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

const getButtonType = computed(() => {
    const buttonColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_button_color || 'teal';
    
    if (buttonColor.startsWith('#')) {
        return 'custom-content';
    } else {
        return buttonColor;
    }
});

const getButtonCorners = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    return corners === 'none' ? 'rounded-sm' : corners;
});

const showBranding = computed(() => {
    const brandingType = landingPageResources.value?.general?.program_branding_type;
    return brandingType === 'symbol' || brandingType === 'logo';
});

const showSymbol = computed(() => {
    return landingPageResources.value?.general?.program_branding_type === 'symbol';
});

const showLogo = computed(() => {
    return landingPageResources.value?.general?.program_branding_type === 'logo';
});

const symbolUrl = computed(() => {
    return portalContentAndDesignStore.srcRoutes?.portal_layout_file_symbol || '';
});

const logoUrl = computed(() => {
    return portalContentAndDesignStore.srcRoutes?.portal_layout_file_logo || '';
});

const showSignupAndLoginLinks = computed(() => {
    return landingPageResources.value?.general?.display_signup_and_login_links;
});

const showRegistrationLink = computed(() => {
    // Check if registration is not disabled
    return !portalContentAndDesignStore.portalLayoutSettings?.portal_page_disable_registration_via_portal;
});

const getLoginRoute = computed(() => {
    return portalContentAndDesignStore.getLoginRoute;
});

const getRegisterRoute = computed(() => {
    return portalContentAndDesignStore.getRegisterRoute;
});

// Editable content
const programTitle = computed({
    get() {
        return landingPageResources.value?.general?.introduction || program.value?.name || '';
    },
    set(value) {
        if (!portalContentAndDesignStore.landingPageResources) {
            portalContentAndDesignStore.landingPageResources = {};
        }
        if (!portalContentAndDesignStore.landingPageResources.general) {
            portalContentAndDesignStore.landingPageResources.general = {};
        }
        portalContentAndDesignStore.landingPageResources.general.introduction = value;
    }
});

const programDescription = computed({
    get() {
        return landingPageResources.value?.general?.description || '';
    },
    set(value) {
        if (!portalContentAndDesignStore.landingPageResources) {
            portalContentAndDesignStore.landingPageResources = {};
        }
        if (!portalContentAndDesignStore.landingPageResources.general) {
            portalContentAndDesignStore.landingPageResources.general = {};
        }
        portalContentAndDesignStore.landingPageResources.general.description = value;
    }
});

const loginButtonText = computed({
    get() {
        return portalContentAndDesignStore.translationSettings?.portal_translation_program_page_log_in || 'Log in';
    },
    set(value) {
        portalContentAndDesignStore.translationSettings.portal_translation_program_page_log_in = value;
    }
});

const signupButtonText = computed({
    get() {
        return portalContentAndDesignStore.translationSettings?.portal_translation_program_page_sign_up || 'Sign up';
    },
    set(value) {
        portalContentAndDesignStore.translationSettings.portal_translation_program_page_sign_up = value;
    }
});
</script>

<template>


    <div :class="getContentContainerClasses">
        <!-- Header section -->
        <div :class="getHeaderClasses">
            <div class="lg:flex gap-6">
                <!-- Branding -->
                <div v-if="showBranding" class="flex-shrink-0">
                    <!-- Symbol -->
                    <div 
                        v-if="showSymbol && symbolUrl" 
                        class="relative h-16 w-16 md:h-20 md:w-20 lg:h-32 lg:w-32"
                        :class="getButtonCorners"
                        :style="`background-image: url('${symbolUrl}'); background-size: cover; background-position: center;`"
                    >
                        <div 
                            class="absolute h-full w-full border border-black border-opacity-10"
                            :class="getButtonCorners"
                        ></div>
                    </div>
                    
                    <!-- Logo -->
                    <div v-if="showLogo && logoUrl" class="relative w-auto" style="max-width: 256px;">
                        <img :src="logoUrl" class="w-auto h-auto" alt="Program logo" />
                    </div>
                </div>

                <!-- Main content -->
                <div class="flex-1 space-y-4 mt-4 lg:mt-0">
                    <div>
                        <div class="flex items-center justify-between gap-6">
                            <h1 
                                class="mb-1 text-xl md:text-2xl lg:text-3xl font-semibold"
                                :class="getTextColorClass"
                            >
                                <editable-text-field
                                    v-model="programTitle"
                                    text-type="span"
                                >
                                </editable-text-field>
                            </h1>
                        </div>
                        
                        <div 
                            v-if="programDescription"
                            class="partnero-page-content-section text-md lg:text-lg space-y-2 text-opacity-75"
                            :class="getTextColorClass"
                        >
                            <editable-text-editor-field
                                v-model="programDescription"
                            >
                            </editable-text-editor-field>
                        </div>
                    </div>

                    <!-- Login and Signup buttons -->
                    <div v-if="showSignupAndLoginLinks" class="flex items-center gap-2">
                        <editable-btn
                            size="small"
                            :class="getButtonCorners"
                            :type="getButtonType"
                            :href="getLoginRoute"
                            v-model="loginButtonText"
                        >
                            {{ loginButtonText }}
                        </editable-btn>
                        
                        <template v-if="showRegistrationLink">
                            <div class="text-sm" :class="getTextColorClass">or</div>
                            <editable-btn
                                size="small"
                                :class="getButtonCorners"
                                :type="getButtonType"
                                :href="getRegisterRoute"
                                :link-target="portalContentAndDesignStore.isExternalRegisterRoute ? '_blank' : '_self'"
                                v-model="signupButtonText"
                            >
                                {{ signupButtonText }}
                            </editable-btn>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Resources section -->
        <referral-portal-resources />
    </div>
</template>
